import { useEffect, useRef, useState } from "react"
import { type LessonMessage, useLessonsMessages } from "../stores/use-lessons-message"
import useCreateStudySession from "../queries/use-create-study-session"
import useUpdateStudySession from "../queries/use-update-study-session"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"

interface UseMessagesProps {
  id: string
  activityId: string
}

export function useMessages({ id, activityId }: UseMessagesProps) {
  const navigate = useNavigate()
  const {
    messages,
    studySessionId,
    setMessages,
    setStudySessionId,
    setDisplayButtons,
    setApproved,
    setIndications,
    setFinalLesson,
  } = useLessonsMessages()
  const [inputMessage, setInputMessage] = useState("")
  const [isTyping, setIsTyping] = useState(false)
  const [failedMessage, setFailedMessage] = useState<string | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)

  const sessionCreated = useRef(false)
  const isInitialLoading = useRef(true)

  const { mutateAsync: createStudySession, isPending: isCreatePending } = useCreateStudySession()
  const { mutateAsync: updateStudySession, isPending: isUpdatePending } = useUpdateStudySession()

  useEffect(() => {
    if (!id || studySessionId || sessionCreated.current) return
    const request = async () => {
      try {
        setIsTyping(true)
        sessionCreated.current = true
        const sessionBody = await createStudySession({ id, activityId })
        setMessages({
          id: sessionBody.id,
          content: sessionBody.agentMessage,
          type: "ai",
        })
        setStudySessionId(sessionBody.id)
      } catch {
        toast.error("Error al crear la sesión de estudio")
      } finally {
        setIsTyping(false)
        isInitialLoading.current = false
      }
    }

    request()
  }, [id, createStudySession])

  function onHandleTextChange(text: string) {
    setInputMessage(text)
  }

  async function sendMessageToBackend(messageContent: string) {
    const userMessageId = crypto.randomUUID()
    const newUserMessage: LessonMessage = {
      id: userMessageId,
      content: messageContent,
      type: "user",
    }

    try {
      setIsTyping(true)
      setMessages(newUserMessage)

      const { agentMessage, displayButtons } = await updateStudySession({
        lessonId: activityId,
        studyLessonId: studySessionId,
        payload: {
          userMessage: messageContent,
          finalLesson: false,
        },
      })

      setMessages({
        id: crypto.randomUUID(),
        content: agentMessage,
        type: "ai",
      })
      setDisplayButtons(displayButtons)
      setFailedMessage(null)
      return true
    } catch {
      // Remove the user message if it failed
      const filteredMessages = messages.filter((msg) => msg.id !== userMessageId)
      // Update the store with the filtered messages
      // This is a workaround since we don't have a way to remove a specific message
      useLessonsMessages.setState({ messages: filteredMessages })

      if (!isRetrying) {
        setFailedMessage(messageContent)
        // Restore the failed message to the input
        setInputMessage(messageContent)
        toast.error("Error al enviar el mensaje", {
          duration: 5000,
          action: {
            label: "Reintentar",
            onClick: () => handleRetry(),
          },
        })
      } else {
        toast.error("Error al enviar el mensaje. Por favor, inténtelo de nuevo.")
      }
      return false
    } finally {
      setIsTyping(false)
      setIsRetrying(false)
    }
  }

  async function handleSendMessage() {
    if (!inputMessage.trim()) return

    const messageToSend = inputMessage
    // Clean the input immediately for better UX
    setInputMessage("")

    await sendMessageToBackend(messageToSend)
  }

  async function handleRetry() {
    if (!failedMessage) return

    setIsRetrying(true)
    const messageToRetry = failedMessage
    setFailedMessage(null)
    // Clean the input in case the failed message was being shown
    setInputMessage("")
    await sendMessageToBackend(messageToRetry)
  }

  async function handleContinueLearning() {
    try {
      setMessages({
        id: crypto.randomUUID(),
        content: "Seguir aprendiendo",
        type: "user",
      })
      setDisplayButtons(false)
      setIsTyping(true)
      const { agentMessage } = await updateStudySession({
        lessonId: id,
        studyLessonId: studySessionId,
        payload: {
          userMessage: "Seguir aprendiendo",
          finalLesson: false,
        },
      })
      setMessages({
        id: crypto.randomUUID(),
        content: agentMessage,
        type: "ai",
      })
      setFinalLesson(false)
    } catch {
      toast.error("Error al continuar aprendiendo. Por favor, inténtelo de nuevo.")
    } finally {
      setIsTyping(false)
    }
  }

  async function handleFinalLesson() {
    try {
      const { approved, indications } = await updateStudySession({
        lessonId: activityId,
        studyLessonId: studySessionId,
        payload: {
          userMessage: "Finalizar lección.",
          finalLesson: true,
        },
      })
      setFinalLesson(true)
      setApproved(approved)
      setIndications(indications)
      navigate(`/activities/lessons/${id}/summary`)
    } catch {
      toast.error("Error al finalizar la lección. Por favor, inténtelo de nuevo.")
    }
  }

  return {
    messages,
    isTyping,
    inputMessage,
    failedMessage,
    isPending: isCreatePending || isUpdatePending,
    onHandleTextChange,
    handleSendMessage,
    handleRetry,
    handleFinalLesson,
    handleContinueLearning,
  }
}
